import React, { useCallback, useEffect, useState } from "react";
import { Container, Spinner, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  intercambiarCodigoOAuth,
  guardarResultadoAuth,
  QUERY_USUARIO_ACTUAL,
} from "../../../services/authServicios";
import { useQueryClient } from "@tanstack/react-query";
import { RUTA_INICIO, RUTA_LOGIN } from "../../../services/rutas";
import { ThemeProvider } from "styled-components";
import { divertidogsTheme } from "../../../styles/divertidogs-theme";

const ERROR_MENSAJES: Record<string, string> = {
  INVALID_EXCHANGE_CODE:
    "El código de inicio de sesión ha expirado o ya fue utilizado. Intente nuevamente.",
  EXCHANGE_CODE_EXPIRED:
    "El código de inicio de sesión ha expirado. Intente nuevamente.",
  EXCHANGE_CODE_ALREADY_USED:
    "El código de inicio de sesión ya fue utilizado. Intente nuevamente.",
  ACCOUNT_LINKING_REQUIRED:
    "La cuenta necesita ser vinculada por un administrador. Inicie sesión con correo y contraseña o contacte al administrador.",
  INVALID_OAUTH_STATE:
    "La sesión de inicio de sesión ya no es válida. Intente nuevamente.",
  GOOGLE_LOGIN_FAILED:
    "La autenticación con Google fue cancelada o falló. Intente nuevamente.",
  INVALID_OAUTH_CALLBACK:
    "La respuesta de inicio de sesión no es válida. Intente nuevamente.",
};

const MENSAJE_GENERICO =
  "Ocurrió un error al iniciar sesión con Google. Intente nuevamente.";

function obtenerMensajeError(codigo: string): string {
  return ERROR_MENSAJES[codigo] || MENSAJE_GENERICO;
}

function OAuthGoogleCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const manejarRetry = useCallback(() => {
    navigate(RUTA_LOGIN, { replace: true });
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get("error");
    const codeParam = params.get("code");

    // Limpia los parámetros de la URL para evitar que al recargar la página se
    // reintente el exchange con un code de un solo uso que ya fue consumido.
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname
    );

    if (errorParam) {
      setError(obtenerMensajeError(errorParam));
      return;
    }

    if (!codeParam) {
      setError(obtenerMensajeError("INVALID_OAUTH_CALLBACK"));
      return;
    }

    setCargando(true);

    intercambiarCodigoOAuth(codeParam)
      .then((respuesta) => {
        guardarResultadoAuth({ token: respuesta.token });
        queryClient.invalidateQueries({ queryKey: [QUERY_USUARIO_ACTUAL] });
        navigate(RUTA_INICIO, { replace: true });
      })
      .catch((err) => {
        setCargando(false);
        setError(err instanceof Error ? err.message : MENSAJE_GENERICO);
      });
  }, [navigate, queryClient]);

  return (
    <ThemeProvider theme={divertidogsTheme}>
      <GlobalSection>
        <StyledContainer>
          <Contenido>
            {cargando && (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden"> iniciando sesión...</span>
                </Spinner>
                <p className="mt-3">Iniciando sesión...</p>
              </div>
            )}
            {error && !cargando && (
              <div className="text-center">
                <p>{error}</p>
                <Button variant="primary" onClick={manejarRetry}>
                  Intentar de nuevo
                </Button>
              </div>
            )}
          </Contenido>
        </StyledContainer>
      </GlobalSection>
    </ThemeProvider>
  );
}

const GlobalSection = styled("section")`
  font-family: ${(props) => props.theme.fontPrimary}, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.8;
  font-weight: normal;
  background: ${(props) => props.theme.colors.light};
  color: #212529;
  height: 100%;
  display: flex;
  align-items: center;
`;

const StyledContainer = styled(Container)`
  max-width: 500px;
`;

const Contenido = styled("div")`
  background: white;
  padding: 2rem;
  border-radius: 5px;
  box-shadow: 0px 10px 34px -15px rgba(0, 0, 0, 0.24);
`;

export default OAuthGoogleCallback;
